import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UserService } from '@app/core/services/UserService';
import { OrganizationService } from '@app/core/services/OrganizationService';
import { IAccountSettings } from '@app/core/interfaces/user/IAccountSettings';
import { IRole } from '@app/core/interfaces/organization/IRole';
import { Subject, Subscription, of } from 'rxjs';
import { finalize, switchMap, takeUntil, timeout } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'edit-user-dialog',
  templateUrl: 'edit-user-modal.component.html',
  styleUrls: ['edit-user-modal.component.scss']
})
export class EditUserModalComponent implements OnInit, OnDestroy {
  user = {} as IAccountSettings;
  organizationId = 0;
  isNewUser = true;
  existingUser = false;
  personVerified = false;
  searching = false;
  saving = false;
  rolesLoading = true;
  errorMessage = '';
  lookupError = '';
  roles: IRole[] = [];
  private lookupSubscription?: Subscription;
  private destroyed$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<EditUserModalComponent>,
    private userService: UserService,
    private organizationService: OrganizationService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.organizationId = this.data.organizationId ?? this.data.user?.OrganizationId ?? 0;
    if (this.data.user != null) {
      this.user = { ...this.data.user,
        RoleIds: [...(this.data.user.RoleIds ?? this.data.user.Roles?.map(role => role.Id) ?? [])] };
      this.isNewUser = false;
    }
    this.organizationService.getRoles(this.organizationId)
      .pipe(takeUntil(this.destroyed$), finalize(() => {
        this.rolesLoading = false;
        this.changeDetector.markForCheck();
      }))
      .subscribe({
        next: roles => {
          this.roles = roles;
          this.user.RoleIds = this.isNewUser
            ? roles.filter(role => role.Name === 'companyadmin').map(role => role.Id)
            : this.user.RoleIds?.filter(id => roles.some(role => role.Id === id));
        },
        error: () => this.errorMessage = 'Rollerna kunde inte hämtas. Stäng och öppna dialogen för att försöka igen.'
      });
  }

  lookupPerson(personalNumber: string) {
    this.lookupSubscription?.unsubscribe();
    this.personVerified = false;
    this.existingUser = false;
    this.lookupError = '';
    this.errorMessage = '';
    this.user.FirstName = '';
    this.user.LastName = '';
    this.user.Email = '';
    if (!/^[0-9]{12}$/.test(personalNumber ?? '')) return;

    this.searching = true;
    let checkingExistingUser = true;
    this.lookupSubscription = this.organizationService.lookupUser(this.organizationId, personalNumber)
      .pipe(
        switchMap(result => {
          checkingExistingUser = false;
          return result.Exists ? of(result)
            : this.userService.getPersonFromSparByPersonalNumber(personalNumber).pipe(timeout(15000));
        }),
        takeUntil(this.destroyed$),
        finalize(() => {
          this.searching = false;
          this.changeDetector.markForCheck();
        })
      )
      .subscribe({
        next: result => {
          this.existingUser = result.Exists === true;
          this.personVerified = this.existingUser || result.hits === 1;
          if (this.personVerified) {
            this.user.FirstName = this.existingUser ? result.FirstName : result.firstName;
            this.user.LastName = this.existingUser ? result.LastName : result.lastName;
            if (this.existingUser) this.user.Email = result.Email;
          } else {
            this.lookupError = 'Personen kunde inte hittas. Kontrollera personnumret.';
          }
        },
        error: error => {
          if (checkingExistingUser && (error.status === 404 || error.status === 405)) {
            this.lookupError = 'Användaruppslagningen saknas i den körande API-versionen. API:t behöver uppdateras och startas om.';
          } else if (error.status === 0) {
            this.lookupError = 'Det gick inte att nå API:t. Kontrollera anslutningen och försök igen.';
          } else if (error.status === 401) {
            this.lookupError = 'Din inloggning har gått ut. Logga in igen.';
          } else if (error.status === 403) {
            this.lookupError = 'Du saknar behörighet att lägga till administratörer i organisationen.';
          } else {
            this.lookupError = error.error?.Message ?? 'Personuppgifterna kunde inte hämtas. Försök igen senare.';
          }
        }
      });
  }

  save(formValid: boolean) {
    if (!formValid || this.saving || this.searching || this.rolesLoading ||
      (this.isNewUser && (!this.personVerified || !this.user.RoleIds?.length))) return;
    this.errorMessage = '';
    this.saving = true;
    this.dialogRef.disableClose = true;
    const request = this.isNewUser
      ? this.organizationService.createUser(this.organizationId, this.user)
      : this.organizationService.updateUser(this.organizationId, this.user);
    request.pipe(takeUntil(this.destroyed$), finalize(() => {
      this.saving = false;
      this.dialogRef.disableClose = false;
      this.changeDetector.markForCheck();
    })).subscribe({
      next: response => this.dialogRef.close(response),
      error: error => this.errorMessage = error.error?.Message ?? 'Det gick inte att spara. Försök igen.'
    });
  }

  cancel() {
    if (!this.saving) this.dialogRef.close();
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.lookupSubscription?.unsubscribe();
  }
}
